import React, { useEffect, useMemo, useState } from 'react'
import { Alert, Badge, Breadcrumb, Button, Card, Col, Container, Form, Row, Spinner } from 'react-bootstrap'
import { ArrowLeft, Edit, Save, Target } from 'lucide-react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import WorkspacePageHeader from '../../components/workspace/WorkspacePageHeader'
import useDocumentTitle from '../../hooks/useDocumentTitle'
import { useAltF1TestFill } from '../../hooks/useAltF1TestFill'
import { dummyPipelineTenderForm } from '../../utils/testFormDummies'
import tenderAPI from '../../services/tenderAPI'
import { uploadTenderDocument } from '../parivartan/tender/documentUpload'
import { platformAPI } from '../../services/platformAPI'
import { loadOrganisationSettings } from '../../utils/organisationSettings'
import PipelineTenderForm from './PipelineTenderForm'
import { resolveMasterValue, useMasterOptions } from '../../components/workspace/MasterLookup'
import {
  emptyPipelineTenderForm,
  formatPipelineCurrency,
  formatPipelineDate,
  formatPipelineLabel,
  mapTenderToForm,
  validatePipelineTenderForm
} from './pipelineTenderUtils'
import { canEditTender } from '../../utils/tenderRecordAccess'
import './PipelineTenderPage.scss'

const DetailField = ({ label, value }) => (
  <div className="pipeline-tender-detail__field">
    <span className="pipeline-tender-detail__label">{label}</span>
    <div className="pipeline-tender-detail__value">{value}</div>
  </div>
)

const PipelineTenderPage = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { tenderId } = useParams()
  const isCreate = location.pathname.endsWith('/new')
  const isEdit = location.pathname.endsWith('/edit')
  const isView = !isCreate && !isEdit
  const inWorkspace = location.pathname.startsWith('/apps/tender')
  const returnTo = location.state?.returnTo || (inWorkspace ? '/apps/tender/register' : '/tender-intelligence/pipeline')
  const backLabel = inWorkspace || String(returnTo).includes('/apps/tender/register') ? 'Back to register' : 'Back to pipeline'

  const user = useSelector((state) => state.auth.user)
  const [team, setTeam] = useState([])
  const [files, setFiles] = useState([])
  const [documentTypes, setDocumentTypes] = useState([])
  const [documentType, setDocumentType] = useState('')
  const [tender, setTender] = useState(location.state?.tender || null)
  const recordLocked = Boolean(tender) && !canEditTender(tender)
  const [formData, setFormData] = useState(emptyPipelineTenderForm())
  const [formErrors, setFormErrors] = useState({})
  const [loading, setLoading] = useState(!isCreate)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const masters = useMasterOptions()

  const applyMasters = (data) => {
    if (!inWorkspace || !masters.ready) return data
    return {
      ...data,
      organization: resolveMasterValue(masters.customers, data.organization),
      serviceType: resolveMasterValue(masters.services, data.serviceType),
      sector: resolveMasterValue(masters.sectors, data.sector),
      tenderType: resolveMasterValue(masters.tenderTypes, data.tenderType),
      source: resolveMasterValue(masters.sources, data.source)
    }
  }

  useAltF1TestFill(isCreate || isEdit, () => {
    setFormData(dummyPipelineTenderForm())
    setFormErrors({})
  })

  useEffect(() => {
    if (!inWorkspace) return undefined
    platformAPI.listReference('document-types', { area: 'tender_registration' })
      .then((response) => {
        const values = (response.data?.data?.values || []).filter((row) => row.status === 'active')
        setDocumentTypes(values)
        setDocumentType((current) => current || values[0]?.code || '')
      })
      .catch(() => setDocumentTypes([]))
    return undefined
  }, [inWorkspace])

  useEffect(() => {
    if (isCreate) {
      let stopped = false
      const apply = (settings) => {
        if (stopped) return
        setFormData({
          ...emptyPipelineTenderForm(),
          ...(inWorkspace ? {
            currency: settings?.currency || 'INR',
            therapeuticArea: 'Other',
            winProbability: '',
            consultancyFeeStatus: 'unknown',
            projectValueStatus: 'unknown',
            consultancyFee: '',
            projectValue: '',
            deadlineTimezone: settings?.timezone || 'Asia/Kolkata',
            submissionTime: ''
          } : {})
        })
        setLoading(false)
      }
      if (inWorkspace) {
        loadOrganisationSettings().then((data) => apply(data?.settings)).catch(() => apply(null))
      } else {
        apply(null)
      }
      return () => { stopped = true }
    }

    let cancelled = false
    ;(async () => {
      setLoading(true)
      setError('')
      try {
        const response = await tenderAPI.getTenderById(tenderId)
        if (!response.success) {
          throw new Error(response.message || 'Failed to load tender')
        }
        const record = response.data?.tender || response.data
        if (!cancelled) {
          setTender(record)
          if (isEdit) {
            setFormData(applyMasters(mapTenderToForm(record)))
          }
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.response?.data?.message || err.message || 'Failed to load tender')
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()

    return () => {
      cancelled = true
    }
  }, [isCreate, isEdit, tenderId, inWorkspace])

  useEffect(() => {
    if (!inWorkspace || !masters.ready || isView) return
    setFormData((prev) => applyMasters(prev))
  }, [inWorkspace, masters.ready, isView, tender])

  useEffect(() => {
    if (!inWorkspace) return undefined
    let cancelled = false
    platformAPI.listTenderTeam().then((response) => {
      if (cancelled) return
      const users = response.data?.data?.users || []
      setTeam(users.map((person) => ({ ...person, id: String(person.id) })))
      if (isCreate) {
        const mine = String(user?._id || user?.id || '')
        setFormData((prev) => ({ ...prev, ownerId: prev.ownerId || mine || (users[0] ? String(users[0].id) : '') }))
      }
    }).catch(() => {
      if (!cancelled) setTeam([])
    })
    return () => { cancelled = true }
  }, [inWorkspace, isCreate, user])

  const pageTitle = useMemo(() => {
    if (isCreate) return 'Create tender'
    if (isEdit && canEditTender(tender)) return 'Edit tender'
    return tender?.title || 'Tender details'
  }, [isCreate, isEdit, tender])

  useDocumentTitle(
    isCreate
      ? 'New tender'
      : (tender?.reference && tender?.title ? `${tender.reference} — ${tender.title}` : pageTitle)
  )

  const handleSubmit = async (event) => {
    event.preventDefault()
    if (isEdit && tender && !canEditTender(tender)) {
      setError('This tender is closed and can no longer be edited.')
      return
    }
    const payload = { ...formData }
    if (inWorkspace) {
      if (!payload.therapeuticArea) payload.therapeuticArea = 'Other'
      if (!payload.currency) payload.currency = 'INR'
      if (payload.financialOpeningAnnounced === 'announced') payload.financialOpeningAnnounced = true
      else if (payload.financialOpeningAnnounced === 'not_announced') payload.financialOpeningAnnounced = false
      else delete payload.financialOpeningAnnounced
      delete payload.workflowStage
      delete payload.pipelineStage
      delete payload.closingOutcome
      delete payload.lifecycle
      delete payload.bidValidityExpiresAt
      delete payload.participationApprovedMode
    }
    const errors = validatePipelineTenderForm(payload, { workspace: inWorkspace })
    setFormErrors(errors)
    if (Object.keys(errors).length > 0) return

    setSaving(true)
    setError('')
    try {
      const response = isCreate
        ? await tenderAPI.createTender(payload)
        : await tenderAPI.updateTender(tenderId, payload)

      if (!response.success) {
        throw new Error(response.message || `Failed to ${isCreate ? 'create' : 'update'} tender`)
      }

      const saved = response.data?.tender
      const savedId = saved?.id || saved?._id || tenderId
      if (isCreate && inWorkspace && files.length && savedId) {
        const failures = []
        const notices = []
        for (const file of files) {
          try {
            const result = await uploadTenderDocument(file, {
              tenderId: savedId,
              typeCode: documentType,
              documentArea: 'tender_registration',
              accessClassification: 'standard'
            })
            if (result.skipped) notices.push({ name: file.name, message: result.message })
          } catch (uploadError) {
            failures.push({ name: file.name, message: uploadError.response?.data?.message || 'Upload failed' })
          }
        }
        navigate(`/apps/tender/register/${savedId}`, {
          state: {
            activeTab: 'documents',
            uploadFailures: failures,
            uploadNotices: notices,
            success: failures.length
              ? 'The tender was saved. Some files did not upload and can be retried on the Documents tab.'
              : 'Tender created successfully'
          }
        })
        return
      }

      navigate(isCreate && inWorkspace && savedId ? `/apps/tender/register/${savedId}` : returnTo, {
        state: {
          success: isCreate ? 'Tender created successfully' : 'Tender updated successfully'
        }
      })
    } catch (err) {
      setError(err.response?.data?.message || err.message || `Failed to ${isCreate ? 'create' : 'update'} tender`)
    } finally {
      setSaving(false)
    }
  }

  const showEditor = isCreate || (isEdit && !recordLocked)
  const subtitle = isCreate
    ? 'Capture the client, submission date, and source. Value, time, team, and documents can be completed later.'
    : isEdit && !recordLocked
      ? 'Update the tender while it is still in progress.'
      : 'This tender is closed. The record is view only.'

  if (loading) {
    return (
      <div className={inWorkspace ? 'tender-form-page tender-form-page--loading' : 'pipeline-tender-page page-enter page-bg-gradient intel-executive-page'}>
        <Spinner animation="border" size="sm" />
      </div>
    )
  }

  return (
    <div className={inWorkspace ? 'tender-form-page' : 'pipeline-tender-page page-enter page-bg-gradient intel-executive-page'}>
      {inWorkspace ? (
        <WorkspacePageHeader
          title={isCreate ? 'New tender' : pageTitle}
          subtitle={subtitle}
          actions={showEditor ? (
            <>
              <Button size="sm" variant="outline-primary" onClick={() => navigate(returnTo)} disabled={saving}>
                Cancel
              </Button>
              <Button size="sm" variant="primary" type="submit" form="tender-record-form" disabled={saving}>
                {saving ? <Spinner animation="border" size="sm" className="me-2" /> : null}
                {isCreate ? 'Create tender' : 'Save changes'}
              </Button>
            </>
          ) : (
            <Button size="sm" variant="outline-primary" onClick={() => navigate(returnTo)}>
              {backLabel}
            </Button>
          )}
        />
      ) : null}
      <Container fluid className={inWorkspace ? 'tender-form-page__body' : undefined}>
        {inWorkspace ? null : (
        <Row className="mb-3">
          <Col>
            <Breadcrumb>
              <Breadcrumb.Item onClick={() => navigate('/tender-intelligence')} style={{ cursor: 'pointer' }}>
                Tender Intelligence
              </Breadcrumb.Item>
              <Breadcrumb.Item onClick={() => navigate('/tender-intelligence/pipeline')} style={{ cursor: 'pointer' }}>
                Pipeline
              </Breadcrumb.Item>
              <Breadcrumb.Item active>{isCreate ? 'New tender' : tender?.reference || 'Tender'}</Breadcrumb.Item>
            </Breadcrumb>
          </Col>
        </Row>
        )}

        {inWorkspace ? null : (
        <div className="intel-executive-page__hero mb-3">
          <div>
            <Button variant="outline-secondary" size="sm" className="mb-2" onClick={() => navigate(returnTo)}>
              <ArrowLeft size={16} className="me-2" />
              {backLabel}
            </Button>
            <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
              <Target size={18} className="text-primary" />
              {tender?.reference ? <Badge bg="light" text="dark">{tender.reference}</Badge> : null}
              {(tender?.workflowStage || tender?.pipelineStage) ? (
                <Badge bg="info">{formatPipelineLabel(tender.workflowStage || tender.pipelineStage)}</Badge>
              ) : null}
            </div>
            <h1>{pageTitle}</h1>
            <p>
              {isCreate
                ? 'Capture a new tender record with commercial, classification, and stage context.'
                : isEdit && !recordLocked
                  ? 'Update the tender record with commercial, classification, and stage fields.'
                  : tender?.organization || 'Review the full tender record without modal constraints.'}
            </p>
          </div>
          <div className="intel-executive-page__hero-actions">
            {tender && !recordLocked && !isEdit && !isCreate ? (
              <Button
                variant="primary"
                onClick={() => navigate(inWorkspace ? `/apps/tender/register/${tenderId}/edit` : `/tender-intelligence/pipeline/${tenderId}/edit`, {
                  state: { returnTo }
                })}
              >
                <Edit size={16} className="me-2" />
                Edit tender
              </Button>
            ) : null}
            {recordLocked ? (
              <small>This tender is closed. The record is view only.</small>
            ) : !isView ? (
              <small>{isCreate ? 'New pipeline record' : 'Editing existing tender'}</small>
            ) : (
              <small>Read-only tender workspace</small>
            )}
          </div>
        </div>
        )}

        {error ? <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert> : null}

        {(isView || (isEdit && tender && !canEditTender(tender))) && tender ? (
          <Row className="g-3">
            <Col xl={8}>
              <Card className="intel-chart-card pipeline-tender-detail">
                <Card.Header className="bg-white fw-semibold">Opportunity narrative</Card.Header>
                <Card.Body>
                  <p className="pipeline-tender-detail__description">{tender.description}</p>
                </Card.Body>
              </Card>

              <Card className="intel-chart-card pipeline-tender-detail mt-3">
                <Card.Header className="bg-white fw-semibold">Identity & scope</Card.Header>
                <Card.Body>
                  <Row className="g-3">
                    <Col md={6}><DetailField label="Reference" value={tender.reference} /></Col>
                    <Col md={6}><DetailField label={inWorkspace ? 'Client' : 'Organization'} value={tender.organization} /></Col>
                    <Col md={6}><DetailField label="Location" value={tender.location} /></Col>
                    <Col md={6}><DetailField label="Source" value={tender.source} /></Col>
                    <Col md={6}><DetailField label="Tender type" value={tender.tenderType} /></Col>
                    {inWorkspace ? <Col md={6}><DetailField label="Service" value={tender.serviceType || '—'} /></Col> : null}
                    {inWorkspace ? <Col md={6}><DetailField label="Sector" value={tender.sector || '—'} /></Col> : null}
                    {inWorkspace ? null : <Col md={6}><DetailField label="Therapeutic area" value={tender.therapeuticArea} /></Col>}
                  </Row>
                </Card.Body>
              </Card>
            </Col>

            <Col xl={4}>
              <Card className="intel-chart-card pipeline-tender-detail">
                <Card.Header className="bg-white fw-semibold">Commercial profile</Card.Header>
                <Card.Body>
                  <DetailField label={inWorkspace ? 'Total project value' : 'Estimated value'} value={inWorkspace ? (tender.projectValueStatus === 'not_disclosed' ? 'Not disclosed' : tender.projectValueStatus === 'stated' && tender.projectValue != null ? formatPipelineCurrency(tender.projectValue, tender.currency) : 'Unknown') : formatPipelineCurrency(tender.estimatedValue, tender.currency)} />
                  {inWorkspace ? <DetailField label="Expected consultancy fee" value={tender.consultancyFeeStatus === 'not_disclosed' ? 'Not disclosed' : tender.consultancyFeeStatus === 'stated' && (tender.consultancyFee != null || tender.estimatedValue != null) ? formatPipelineCurrency(tender.consultancyFee ?? tender.estimatedValue, tender.currency) : 'Unknown'} /> : null}
                  <DetailField label="Submission" value={tender.deadlineTimeKnown && tender.submissionTime ? `${formatPipelineDate(tender.submissionDate || tender.deadline)} ${tender.submissionTime} ${tender.deadlineTimezone || ''}`.trim() : `${formatPipelineDate(tender.submissionDate || tender.deadline)}${tender.timeConfirmationNeeded ? ' · time not recorded' : ''}`} />
                  <DetailField label="Technical opening" value={tender.technicalOpeningAt ? formatPipelineDate(tender.technicalOpeningAt) : 'Not recorded'} />
                  <DetailField label="Financial opening" value={tender.financialOpeningAnnounced === false ? 'Not announced' : tender.financialOpeningAnnounced === true && tender.financialOpeningAt ? formatPipelineDate(tender.financialOpeningAt) : 'Not recorded'} />
                  <DetailField label="Bid validity" value={tender.bidValidityExpiresAt ? `Expires ${formatPipelineDate(tender.bidValidityExpiresAt)}` : tender.bidValidityDays ? `${tender.bidValidityDays} days, reference not complete` : 'Not recorded'} />
                  <DetailField label="Participation" value={tender.participationMode === 'solo' ? 'Solo' : tender.participationMode === 'jv_lead' ? 'JV lead' : tender.participationMode === 'undecided' ? 'Undecided' : 'Not recorded'} />
                  <DetailField label="Days remaining" value={tender.daysUntilDeadline ?? '—'} />
                </Card.Body>
              </Card>

              <Card className="intel-chart-card pipeline-tender-detail mt-3">
                <Card.Header className="bg-white fw-semibold">{inWorkspace ? 'Tender stage' : 'Pursuit governance'}</Card.Header>
                <Card.Body>
                  <DetailField label="Journey" value={tender.lifecycle?.positionLabel || formatPipelineLabel(tender.workflowStage || tender.pipelineStage)} />
                  {tender.lifecycle?.reviewNote ? <DetailField label="Review" value={tender.lifecycle.reviewNote} /> : null}
                  {tender.lifecycle?.outcomeLabel ? <DetailField label="Outcome" value={tender.lifecycle.outcomeLabel} /> : null}
                  {tender.outcomeReasonLabel ? <DetailField label="Reason" value={tender.outcomeReasonLabel} /> : null}
                  {tender.outcomeRemarks ? <DetailField label="Remarks" value={tender.outcomeRemarks} /> : null}
                  <DetailField label="Priority" value={formatPipelineLabel(tender.priority)} />
                  <DetailField label="Urgency" value={formatPipelineLabel(tender.urgency)} />
                  <DetailField label="Win probability" value={tender.winProbability == null || tender.winProbability === '' ? 'Not set' : `${tender.winProbability}%`} />
                  <DetailField label="AI match score" value={`${tender.aiMatchScore || 0}%`} />
                  <DetailField label="Owner" value={tender.owner?.name || 'Unassigned'} />
                </Card.Body>
              </Card>

              <Card className="intel-chart-card pipeline-tender-detail mt-3">
                <Card.Header className="bg-white fw-semibold">Tags</Card.Header>
                <Card.Body>
                  {tender.tags?.length ? (
                    <div className="d-flex flex-wrap gap-2">
                      {tender.tags.map((tag) => (
                        <Badge key={tag} bg="secondary">{tag}</Badge>
                      ))}
                    </div>
                  ) : (
                    <p className="text-muted mb-0">No tags assigned</p>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        ) : (
          <Form id="tender-record-form" onSubmit={handleSubmit}>
            <Card className="intel-chart-card pipeline-tender-editor">
              {inWorkspace ? null : (
              <Card.Header className="bg-white d-flex flex-wrap justify-content-between align-items-center gap-2">
                <span className="fw-semibold">{isCreate ? 'New tender workspace' : 'Edit tender workspace'}</span>
                <div className="d-flex flex-wrap gap-2">
                  <Button variant="outline-secondary" onClick={() => navigate(returnTo)}>
                    Cancel
                  </Button>
                  <Button variant="primary" type="submit" disabled={saving}>
                    {saving ? <Spinner animation="border" size="sm" className="me-2" /> : <Save size={16} className="me-2" />}
                    {isCreate ? 'Create tender' : 'Update tender'}
                  </Button>
                </div>
              </Card.Header>
              )}
              <Card.Body>
                <PipelineTenderForm
                  formData={formData}
                  formErrors={formErrors}
                  onChange={setFormData}
                  variant={inWorkspace ? 'workspace' : 'default'}
                  masters={masters}
                  team={team}
                  onFiles={isCreate && inWorkspace ? setFiles : undefined}
                  documentTypes={documentTypes}
                  documentType={documentType}
                  onDocumentType={setDocumentType}
                />
              </Card.Body>
            </Card>
          </Form>
        )}
      </Container>
    </div>
  )
}

export default PipelineTenderPage
